import React, { useContext, useEffect, useState } from 'react';
import { NavLink } from 'react-router-dom';
import logo from '@shared/assets/logos/escort_logo.svg';
import collapseIcon from '@shared/assets/sprites/sidebar-collapse.svg';
import { NAV_ITEMS, NAV_ITEMS_USER } from '@shared/config/navigation';
import { useSelector } from 'react-redux';
import { useAppDispatch } from '@app/store/store';
import { getCart, selectCartItemsLength } from '../../../entities/cart/model';
import {
  fetchPendingJoinRequestsCount,
  selectJoinRequestsCount,
} from '../../../entities/joinrequest/model';
import { AbilityContext, Can } from '@shared/lib/ability';
import { User } from '../../../types/interfaces';

interface SidebarProps {
  hasCompany: boolean;
  me: User;
}

interface NavItem {
  id: number;
  icon: string;
  alt: string;
  link: string;
  text: string;
  perm_name: string;
  action?: string;
  name?: string;
}

const Sidebar = ({ hasCompany, me }: SidebarProps) => {
  const [isExpanded, setIsExpanded] = useState(
    localStorage.getItem('isExpanded') === 'true' || false
  );
  const dispatch = useAppDispatch();
  const ability = useContext(AbilityContext);
  const cartItemsLength = useSelector(selectCartItemsLength);
  const joinRequestsCount = useSelector(selectJoinRequestsCount);

  // Бейдж и подсчёт заявок имеют смысл только для тех, кто может
  // эти заявки реально принимать/отклонять — это owner/админ
  // компании (тот же гейт, что и на табе «Запросы на присоединение»
  // в Company.tsx — `ability.can('view', 'invite')`). Обычный
  // сотрудник видеть уведомление не должен: у него нет UI для
  // обработки запроса, иконка только путала. Ниже мы используем
  // этот флаг и для рендера бейджа, и для решения, нужно ли вообще
  // дёргать /joinrequests/ — иначе сотрудник видел в нетворке и
  // запрос, и его response.
  const canManageJoinRequests = ability.can('view', 'invite');

  useEffect(() => {
    localStorage.setItem('isExpanded', String(isExpanded));
    dispatch(getCart());
  }, [isExpanded, dispatch]);

  // Подтягиваем счётчик активных запросов на присоединение для
  // компании текущего юзера. Дёргаем только при наличии компании —
  // /joinrequests/?company= для null отдаст не то, что нужно. После
  // accept/reject в Company.tsx этот же thunk перезапускается оттуда,
  // чтобы бейдж в сайдбаре синхронно обновился.
  // Дополнительно гейтим по `canManageJoinRequests`, чтобы рядовой
  // сотрудник вообще не делал запрос (иначе он засветится в DevTools
  // и будет вызывать у юзера вопросы — см. соответствующий баг).
  useEffect(() => {
    if (canManageJoinRequests && me?.company?.id) {
      dispatch(fetchPendingJoinRequestsCount(me.company.id));
    }
  }, [dispatch, canManageJoinRequests, me?.company?.id]);

  const isBasicGroupUser = me?.groups?.[0] === 'BASIC';
  const isManager = me?.groups?.[0] === 'MANAGER';

  // Любые внутренние FMeter-роли (менеджмент / суппорт / админы) —
  // им NAV_ITEMS_USER не показываем никогда. Всех остальных, у кого
  // нет привязанной компании, считаем «клиентской стороной»: им нужен
  // пункт «Компания» в сайдбаре, чтобы подать запрос на присоединение.
  // Раньше тут была завязка на `groups[0] === 'BASIC'`, но это
  // ломалось, если `groups` пуст или BASIC не на первой позиции
  // (legacy-аккаунты, мульти-группы), — юзер не видел нужный пункт.
  const STAFF_OR_MANAGER_GROUPS = [
    'MANAGER',
    'OFFICE_MANAGER',
    'SALES_DIRECTOR',
    'admins',
    'HD_HEAD',
    'HD_ENGINEER',
    'HD_SERVICE_ENGINEER',
  ];
  const isStaffOrInternal =
    me?.is_staff ||
    me?.is_superuser ||
    (me?.groups ?? []).some((g) => STAFF_OR_MANAGER_GROUPS.includes(g));

 
  const isClientSideUser = hasCompany || !isStaffOrInternal;

 
  const hasOkdeskAccess = isStaffOrInternal || Boolean(me?.company);

  const isBasicWithoutCompany = !hasCompany && !isStaffOrInternal;

  const navItems = (isClientSideUser ? NAV_ITEMS_USER : NAV_ITEMS)
    .filter((item) => {
      // id 10 — пункт «Устройства» в обоих списках. См. shared/config/navigation.
      if (item.id === 10 && !hasOkdeskAccess) return false;
      // id 13 — пункт «Поддержка». Прячем для BASIC-юзера без компании.
      if (item.id === 13 && isBasicWithoutCompany) return false;
      return true;
    })
    .map((item) => {
  
      if (
        isBasicWithoutCompany &&
        (item.id === 7 || item.id === 11)
      ) {
        return { ...item, perm_name: 'all' };
      }
      return item;
    });

  return (
    <div className="flex h-screen sticky left-0 top-0">
      <div className={`relative bg-white transition-all duration-200 ${isExpanded ? 'w-[239px]' : 'w-[82px]'}`}>
        <div className="border-b border-t border-t-[transparent] border-[#e5e7eb] py-4">
          <div
            className={`flex items-center min-h-[44px] w-full ${
              isExpanded
                ? 'justify-between pl-[30px] pr-[20px]'
                : 'justify-center'
            }`}
          >
            <img
              src={logo}
              alt="Logo"
              className={`h-[44px] ${!isExpanded ? 'hidden' : ''}`}
            />
            <button
              type="button"
              data-qa="sidebar_toggle"
              className="cursor-pointer z-10 p-1 -m-1 text-gray-500 hover:text-gray-700 transition-colors"
              onClick={() => setIsExpanded(!isExpanded)}
              aria-label={isExpanded ? 'Свернуть меню' : 'Развернуть меню'}
              title={isExpanded ? 'Свернуть' : 'Развернуть'}
            >
              <img
                src={collapseIcon}
                alt=""
                aria-hidden
                className={`w-6 h-6 transition-transform duration-200 ${
                  isExpanded ? '' : 'rotate-180'
                }`}
              />
            </button>
          </div>
        </div>
        <div className="flex flex-col items-start pt-[21px] overflow-hidden">
          {navItems.map((item: NavItem, index: number) => (
            <HasCan
              key={item.id}
              I={item.action}
              item={item}
              a={item.perm_name}
              isBasicGroupUser={isBasicGroupUser}
              isManager={isManager}
              hasCompany={hasCompany}
            >
              <Tab item={item} isExpanded={isExpanded} index={index}>
                {item.alt === 'Корзина' && cartItemsLength > 0 && (
                  <span
                    className={`absolute flex justify-center items-center w-5 h-5 rounded-full bg-red-500 text-white text-xs font-bold z-10 top-[-10px] ${
                      !isExpanded ? 'left-[20px]' : 'right-[-20px]'
                    }`}
                  >
                    {cartItemsLength}
                  </span>
                )}
             
                {/*
                  Бейдж активных запросов на присоединение к компании.
                  Показываем только тем, кто реально может их обработать
                  (тот же CASL-гейт, что и таб «Запросы на присоединение»
                  в Company.tsx — `view invite`). Без этой проверки рядовой
                  сотрудник видел иконку уведомления у поля «Компания»,
                  хотя ничего сделать с запросом не мог — это путало.
                  Дополнительно служит страховкой от стейл-стейта редакса,
                  если значение успело попасть туда от прошлого юзера
                  в рамках одной SPA-сессии.
                */}
                {item.alt === 'Компания' &&
                  canManageJoinRequests &&
                  joinRequestsCount > 0 && (
                    <span
                      className={`absolute flex justify-center items-center w-5 h-5 rounded-full bg-red-500 text-white text-xs font-bold z-10 top-[-10px] ${
                        !isExpanded ? 'left-[20px]' : 'right-[-20px]'
                      }`}
                    >
                      {joinRequestsCount}
                    </span>
                  )}
              </Tab>
            </HasCan>
          ))}
        </div>
      </div>
    </div>
  );
};

export default Sidebar;

interface HasCanProps {
  I?: string;
  item: NavItem;
  a: string;
  children: React.ReactNode;
  isBasicGroupUser: boolean;
  isManager: boolean;
  hasCompany?: boolean;
}

function HasCan({ I = 'view', a, children }: HasCanProps) {
  if (a === 'all') return <>{children}</>;
  return <Can I={I} a={a}>{children}</Can>;
}

interface TabProps {
  item: NavItem;
  isExpanded: boolean;
  index: number;
  children: React.ReactNode;
}

// Стабильный QA-идентификатор пункта меню строим из его роута, а не из
// локализованного текста: текст может меняться, link — нет. Корень '/'
// маппим в 'home', остальные слэши заменяем на подчёркивания.
function navQaSlug(link: string): string {
  const trimmed = link.replace(/^\/+|\/+$/g, '');
  return trimmed === '' ? 'home' : trimmed.replace(/\//g, '_');
}

function Tab({ item, isExpanded, index, children }: TabProps) {
  return (
    <div className="relative w-full group" title={!isExpanded ? item.text : undefined}>
      <NavLink
        state={item.name}
        to={item.link}
        data-qa={`sidebar_nav_${navQaSlug(item.link)}`}
        className="no-underline flex items-center justify-start w-full relative mt-8 pl-[30px] cursor-pointer"
      >
        {({ isActive }) => (
          <>
            <div className="flex items-start justify-start relative">
              {/* Бейджи выводим для пунктов, где они вообще могут быть.
                  Tab принимает все возможные бейджи через children,
                  но включаются только нужные `item.alt === ...`-ветки
                  родителя (см. Sidebar render выше). */}
              {(item.alt === 'Корзина' || item.alt === 'Компания') && (
                <div className="contents">
                  {React.Children.toArray(children)}
                </div>
              )}
              <img
                src={item.icon}
                alt={item.alt}
                className={`transition-all w-[24px] h-[24px] ${item.id === 10 ? '-ml-[2.5px]' : ''} ${
                  isActive ? '[filter:invert(37%)_sepia(93%)_saturate(7471%)_hue-rotate(356deg)_brightness(91%)_contrast(135%)]' : ''
                }`}
              />
              <p className={`m-0 ml-3 font-medium text-[16px] leading-[30px] whitespace-nowrap transition-all ${
                isExpanded ? 'visible opacity-100' : 'invisible opacity-0'
              } ${isActive ? 'text-black' : 'text-[#C4C4C4]'} group-hover:text-black`}>
                {item.text}
              </p>
            </div>
            {isExpanded && (
              <div className={`absolute bottom-0 right-0 w-1 h-full bg-[#E3282A] transition-transform duration-300 ease-in-out ${
                isActive ? 'scale-x-100 z-10' : 'scale-x-0 -z-10'
              } origin-left`} />
            )}
          </>
        )}
      </NavLink>
    </div>
  );
}
